import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables';
import { postEppMaintenance } from '../../common/fetch';
import { companyNameMsg, deviceMsg, faultDescriptionPlaceHolderMsg, t, userMsg } from '../../common/i18n/index.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { TextAreaComponent } from '../../common/react-hook-form/components/TextAreaComponent.js';
import { ScrollToTop } from '../../common/utils/browserUtils.js';
import { PostEppMaintenanceRequest } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { Loading } from '../Loading/index.js';
import { pictureStorageBaseUrl } from '../Picture/contentfulImageProxy';
import { DeliveryAddressDetails } from './DeliveryAddressDetails.js';
import { EmployeeSupportOrServiceHeader } from './EmployeeSupportOrServiceHeader.js';
import { SupportOrServiceConfirmationDialog } from './SupportOrServiceConfirmationDialog';
import { getFullNameFromAuthenticatedUser } from './eppMaintenanceUtils.js';
import type { DamageDescriptionTypes } from './EppSupportOrServiceAnnouncement';
import type { CompanyInfoResponse, DeliveryAddress, Subscription } from '../../generated/api/models.js';

import './EppSupportOrServiceRequestForm.scss';

export interface EppSupportOrServiceRequestFormProps {
  onCancel: () => void;
  subscription: Subscription;
  isEmployee: boolean;
  damageDescriptionType: DamageDescriptionTypes | undefined;
  companyInfo?: CompanyInfoResponse;
}

type EppSupportOrServiceRequestFormValues = {
  faultDescription: string;
  deviceCondition?: PostEppMaintenanceRequest.DeviceConditionEnum;
};

const damageTypeTranslations = {
  SCREEN_BROKEN: t.LLYE('Broken screen and/or back glass'),
  CAMERA_ISSUE: t.ASOX('Camera problem'),
  CHARGING_ISSUE: t.DOV9('Charging problem'),
  HEADPHONES_ISSUE: t.Z9X2('Earpiece/microphone problem'),
  BATTERY_ISSUE: t.XCJ3('Battery'),
  OTHER_ISSUE: t.C6WY('Other'),
};

const ServiceRequestInfo = () => (
  <div className="of-epp-maintenance-service-request-form--radio-group-header">
    <span className="of-epp-maintenance-service-request-form--radio-group-header__title">
      {t.EQI1('Defective device condition grade')}
    </span>
    <span>{t.AEOA('Choose the option that best describes the current state of your device')}</span>
  </div>
);

const DeviceConditionSelection = () => (
  <div className={dsClass.PADDING_BOTTOM_2}>
    <SelectRadio
      name="deviceCondition"
      items={[
        {
          label: t.U1IQ('Good as new'),
          value: PostEppMaintenanceRequest.DeviceConditionEnum.GOOD_AS_NEW,
        },
        {
          label: t.MBNB('Scratches or wear on the screen'),
          value: PostEppMaintenanceRequest.DeviceConditionEnum.SCRATCHES_ON_SCREEN,
        },
        {
          label: t.QNUL('Scratches or wear on the body'),
          value: PostEppMaintenanceRequest.DeviceConditionEnum.SCRATCHES_ON_BODY,
        },
        {
          label: t.HVG8('Scratches or wear on the screen and body'),
          value: PostEppMaintenanceRequest.DeviceConditionEnum.SCRATCHES_ON_BODY_AND_SCREEN,
        },
        {
          label: t.PO7A('Damage case (damage to the device, broken screen, warped body, wet, etc.)'),
          value: PostEppMaintenanceRequest.DeviceConditionEnum.INCIDENT,
        },
      ]}
      required
    />
  </div>
);

const FaultDescription = () => (
  <div className="of-epp-maintenance-service-request-form__message">
    <p>{t.TQ6Z('Additional details about the defect')}</p>
    <TextAreaComponent
      name="faultDescription"
      placeholder={t.U1WD(faultDescriptionPlaceHolderMsg)}
      label=""
      required
      disabled={false}
    />
  </div>
);

const DeviceDetails = ({
  companyName,
  subscriptionName,
  fullName,
}: {
  subscriptionName: string;
  fullName: string;
  companyName?: string;
}) => (
  <div className={dsClass.PADDING_TOP_7}>
    <div className={dsClass.GRID_ROW}>
      <div
        className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_6} of-epp-maintenance-service-request-form-device_details-value`}
      >
        <strong>{t.AJ93(companyNameMsg)}</strong>
        {companyName}
      </div>
      <div
        className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_6} of-epp-maintenance-service-request-form-device_details-value`}
      >
        <strong>{t.LUZM(userMsg)}</strong>
        {fullName}
      </div>
    </div>
    <div className={dsClass.GRID_ROW}>
      <div
        className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_6} of-epp-maintenance-service-request-form-device_details-value`}
      >
        <strong>{t.TPVQ(deviceMsg)}</strong>
        {subscriptionName || '-'}
      </div>
    </div>
  </div>
);

export const EppSupportOrServiceRequestForm = ({
  subscription,
  onCancel,
  isEmployee,
  companyInfo,
  damageDescriptionType,
}: EppSupportOrServiceRequestFormProps) => {
  const navigate = useNavigate();
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { authenticatedUser } = useAuth();
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>();
  const fullName = getFullNameFromAuthenticatedUser(authenticatedUser);

  const handleSubmit = async (values: EppSupportOrServiceRequestFormValues) => {
    const faultDescription = damageDescriptionType
      ? `${damageTypeTranslations[damageDescriptionType]}:\n\n${values?.faultDescription}`
      : values?.faultDescription;

    const payload: PostEppMaintenanceRequest = {
      deliveryAddress: deliveryAddress!, // submit will be disabled unless the deliveryAddress is defined
      deviceCondition: values.deviceCondition!, // validation prevents submit until a selection is made
      faultDescription,
    };
    await postEppMaintenance(subscription.subscriptionId, payload);
    await navigate(isEmployee ? paths.EMPLOYEE_DEVICES : paths.PS_DEVICES);
    addNotification(t.DUFL('Your device service request was submitted successfully'));
  };

  const methods = useForm<EppSupportOrServiceRequestFormValues>({
    mode: 'onTouched',
    defaultValues: {
      faultDescription: '',
    },
  });

  if (!companyInfo) {
    return <Loading />;
  } else {
    return (
      <FormProvider {...methods}>
        {/* Can't use form tags here because there is another form used in child component DeliveryAddressDetails /
      DeliveryAddressForm and nested forms are not allowed in HTML */}
        <div className="of-epp-maintenance-service-request-form">
          {/* TODO can be removed when the ScrollToTop component works up in the component hierarchy. The current
            solution needs more widely spread React Router usage.
            More info in the ScrollToTop code starting the from https://github.devcloud.elisa.fi/onlinefirst/online/blob/master/online-ui/src/components/common/utils/browserUtils.tsx#L74
            */}
          <ScrollToTop />
          {isEmployee && <EmployeeSupportOrServiceHeader />}
          <ServiceRequestInfo />
          <DeviceConditionSelection />
          <FaultDescription />
          {!isEmployee && (
            <DeviceDetails
              subscriptionName={subscription.subscriptionName}
              companyName={companyInfo?.companyName}
              fullName={fullName}
            />
          )}
          <DeliveryAddressDetails
            address={companyInfo?.address}
            companyName={companyInfo?.companyName}
            recipientName={fullName}
            phoneNumber={authenticatedUser?.mobile || ''}
            setDeliveryAddress={setDeliveryAddress}
            showConfirmAddressCheckbox={true}
            onAddressReady={setIsSubmitEnabled}
            isSupportRequest={true}
          />
          {subscription.subscriptionName.toLowerCase().includes('apple') && (
            <CL.Disclaimer
              className="of-epp-maintenance-service-request-form__banner"
              icon={<CL.Icon icon="notification" />}
              visible={true}
            >
              <b>{t.U9GR('Disable Find My feature')}</b>
              <br />
              {t.W5Q6(
                'Before ordering a device replacement, the Find My feature must be disabled on Apple devices. An active feature prevents any service actions/repairs on the device.'
              )}
              <br />
              <a
                href={t.ERA0('https://support.apple.com/en-euro/guide/icloud/mmfc0eeddd/icloud')}
                target="_blank"
                rel="noreferrer"
              >
                {t.C98K('Instructions on Apple’s support page')}
              </a>
            </CL.Disclaimer>
          )}
          <hr />
          <ButtonGroupForSubmitAndBack
            className="of-epp-maintenance-service-request-form__buttons"
            onSubmit={() => {
              const isAppleSubscription = subscription.subscriptionName.toLowerCase().includes('apple');
              methods.handleSubmit(values => {
                if (isAppleSubscription) {
                  setShowConfirmDialog(true);
                } else {
                  handleSubmit(values);
                }
              })();
            }}
            onCancel={onCancel}
            submitDisabled={!isSubmitEnabled || !deliveryAddress}
            submitting={methods.formState.isSubmitting}
          />
          {showConfirmDialog && (
            <SupportOrServiceConfirmationDialog
              icon={
                <img alt="Icon" src={`${pictureStorageBaseUrl}18h18UrHGnKJSZVn157DCk/SelfService.png`} width="100" />
              }
              header={t.CUJO('Have you disabled the Find My feature?')}
              body={t.TGK3('An active Find My feature for Apple prevents any service actions/repairs on the device.')}
              onCancel={() => setShowConfirmDialog(false)}
              onConfirm={onCloseDialog => {
                methods.handleSubmit(handleSubmit)();
                onCloseDialog();
              }}
            />
          )}
        </div>
      </FormProvider>
    );
  }
};
